import {
    AhaMomentImage,
    type AhaMomentIntake,
    BRANCH_SLIDES,
    CMMBranch,
    type DebtIntakeEntry,
    HeadlineType,
    type MoneyMapNumbers,
    ResultSlide,
    RuleSetType,
    RuleType,
    type SavingsIntakeEntry,
} from '@fruitful/types';

import {
    DEPOSIT_APY_PERCENT,
    HIGH_INTEREST_THRESHOLD,
    INVESTMENT_RETURN_PERCENT,
    IRA_ANNUAL_LIMIT_CENTS,
    MAX_SIMULATION_MONTHS,
} from './constants';
import { runSimulation } from './engine';
import {
    buildInvestmentWaterfall,
    formatDollarsLocal,
} from './investmentWaterfall';
import {
    type Account,
    type DebtAccount,
    type MoneyMapSimulation,
    type Rule,
    type SimulationParams,
} from './types';
import { MoneyMapSimulationSchema } from './validation';
import { formatSaveForGoalLabel } from './withArticle';

export {
    DEPOSIT_APY_PERCENT,
    HIGH_INTEREST_THRESHOLD,
    INVESTMENT_RETURN_PERCENT,
    IRA_ANNUAL_LIMIT_CENTS,
    MAX_SIMULATION_MONTHS,
} from './constants';

const MAX_REASONABLE_TARGET_CENTS = 20_000_000; // $200k

/**
 * Pick the savings goal with the highest targetAmountCents that is at or below
 * $200k. If every goal exceeds $200k, pick the lowest instead.
 */
const pickHighestTargetGoal = <T extends { targetAmountCents: number }>(
    goals: T[] | null | undefined,
): T | undefined => {
    if (goals == null || goals.length === 0) return undefined;
    const under = goals.filter(
        (g) => g.targetAmountCents <= MAX_REASONABLE_TARGET_CENTS,
    );
    if (under.length > 0) {
        return under.reduce((best, g) =>
            g.targetAmountCents > best.targetAmountCents ? g : best,
        );
    }
    return goals.reduce((best, g) =>
        g.targetAmountCents < best.targetAmountCents ? g : best,
    );
};

/* ─── Debt waterfall split percentages ───
 *
 * When high-interest debt (APR >= 10%) is present, the waterfall
 * aggressively prioritizes paydown. When only low-interest debt
 * remains, funds are balanced more evenly across goals.
 *
 * Each debt's minimumPaymentCents is applied by the engine each month
 * before allocating goals funds — this represents the payment already
 * covered by bills. The avalanche rule then directs extra principal
 * from the goals pool to the highest-APR debt first, cascading down.
 *
 * All debt types (credit cards, personal loans, student loans, car
 * loans, mortgages) use the same model: monthly interest accrual +
 * background payment + extra from goals pool. APR-based priority
 * ordering naturally puts low-rate debt (e.g. mortgage) last.
 */

/** High-interest debt present: aggressive paydown */
export const HIGH_INTEREST_DEBT_SPLIT = 0.8;
export const HIGH_INTEREST_EF_SPLIT = 0.15;
export const HIGH_INTEREST_RETIREMENT_SPLIT = 0.05;

/** No high-interest debt: balanced growth */
export const LOW_INTEREST_DEBT_SPLIT = 0.4;
export const LOW_INTEREST_EF_SPLIT = 0.3;
export const LOW_INTEREST_RETIREMENT_SPLIT = 0.3;

/** Savings path + high-interest debt: aggressive paydown with savings goal */
export const SAVINGS_HI_DEBT_SPLIT = 0.7;
export const SAVINGS_HI_SAVINGS_SPLIT = 0.15;
export const SAVINGS_HI_EF_SPLIT = 0.1;
export const SAVINGS_HI_RETIREMENT_SPLIT = 0.05;

/** Savings path + low-interest debt: balanced paydown with savings goal */
export const SAVINGS_LO_DEBT_SPLIT = 0.45;
export const SAVINGS_LO_SAVINGS_SPLIT = 0.1;
export const SAVINGS_LO_EF_SPLIT = 0.4;
export const SAVINGS_LO_RETIREMENT_SPLIT = 0.05;

/** Savings path, no debt */
export const SAVINGS_ONLY_EF_SPLIT = 0.5;
export const SAVINGS_ONLY_SAVINGS_SPLIT = 0.4;
export const SAVINGS_ONLY_RETIREMENT_SPLIT = 0.1;

/** No debt, no savings: EF + retirement only */
export const NO_DEBT_EF_SPLIT = 0.6;
export const NO_DEBT_RETIREMENT_SPLIT = 0.4;

/* ─── Scenario split resolution ─── */

export interface ScenarioSplits {
    debt: number;
    savings: number;
    ef: number;
    retirement: number;
}

/**
 * Single source of truth for which split percentages apply given a
 * user's scenario. Both the simulation builders and the static
 * fallback display call this so the logic can never drift.
 */
export function getScenarioSplits(scenario: {
    hasDebts: boolean;
    hasSavings: boolean;
    hasHighInterest: boolean;
}): ScenarioSplits {
    const { hasDebts, hasSavings, hasHighInterest } = scenario;

    if (hasSavings && hasDebts) {
        return {
            debt: hasHighInterest
                ? SAVINGS_HI_DEBT_SPLIT
                : SAVINGS_LO_DEBT_SPLIT,
            savings: hasHighInterest
                ? SAVINGS_HI_SAVINGS_SPLIT
                : SAVINGS_LO_SAVINGS_SPLIT,
            ef: hasHighInterest ? SAVINGS_HI_EF_SPLIT : SAVINGS_LO_EF_SPLIT,
            retirement: hasHighInterest
                ? SAVINGS_HI_RETIREMENT_SPLIT
                : SAVINGS_LO_RETIREMENT_SPLIT,
        };
    }

    if (hasSavings) {
        return {
            debt: 0,
            savings: SAVINGS_ONLY_SAVINGS_SPLIT,
            ef: SAVINGS_ONLY_EF_SPLIT,
            retirement: SAVINGS_ONLY_RETIREMENT_SPLIT,
        };
    }

    if (!hasDebts) {
        return {
            debt: 0,
            savings: 0,
            ef: NO_DEBT_EF_SPLIT,
            retirement: NO_DEBT_RETIREMENT_SPLIT,
        };
    }

    return {
        debt: hasHighInterest
            ? HIGH_INTEREST_DEBT_SPLIT
            : LOW_INTEREST_DEBT_SPLIT,
        savings: 0,
        ef: hasHighInterest ? HIGH_INTEREST_EF_SPLIT : LOW_INTEREST_EF_SPLIT,
        retirement: hasHighInterest
            ? HIGH_INTEREST_RETIREMENT_SPLIT
            : LOW_INTEREST_RETIREMENT_SPLIT,
    };
}

/* ─── Intake interfaces ─── */

interface DebtBranchIntake {
    monthlyExpensesCents: number;
    monthlySpendCents: number;
    monthlyGoalsSpendCents: number;
    debts: DebtIntakeEntry[];
    debtTypes: string[];
}

interface SavingsBranchIntake {
    monthlyExpensesCents: number;
    monthlySpendCents: number;
    monthlyGoalsSpendCents: number;
    savings: SavingsIntakeEntry[];
    debts?: DebtIntakeEntry[] | null;
    debtTypes?: string[] | null;
}

export type { DebtIntakeEntry, SavingsIntakeEntry };

/* ─── Public API ─── */

/**
 * Build a MoneyMapSimulation from intake data, dispatching to the
 * branch-specific builder based on intake.branch.
 */
export function buildSimulation(
    intake: AhaMomentIntake,
): MoneyMapSimulation | null {
    let simulation: MoneyMapSimulation | null = null;

    switch (intake.branch) {
        case CMMBranch.StartInvesting:
            return null;
        case CMMBranch.GoalsSavings:
            if (intake.savings == null) return null;
            simulation = buildSavingsSimulation({
                ...intake,
                savings: intake.savings,
            });
            break;
        case CMMBranch.GoalsSystem:
            simulation = buildSystemSimulation(intake);
            break;
        case CMMBranch.MoneySystem:
            if (intake.savings != null) {
                simulation = buildSavingsSimulation({
                    ...intake,
                    savings: intake.savings,
                });
            } else if (intake.debts != null && intake.debtTypes != null) {
                simulation = buildDebtSimulation({
                    ...intake,
                    debts: intake.debts,
                    debtTypes: intake.debtTypes,
                });
            }
            break;
        case CMMBranch.GoalsDebt:
        default: {
            const hasSavings =
                intake.savings != null && intake.savings.length > 0;
            if (hasSavings && intake.savings != null) {
                simulation = buildSavingsSimulation({
                    ...intake,
                    savings: intake.savings,
                });
            } else {
                simulation = buildDebtSimulation({
                    ...intake,
                    debts: intake.debts ?? [],
                    debtTypes: intake.debtTypes ?? [],
                });
            }
            break;
        }
    }

    if (simulation == null) return null;

    const result = MoneyMapSimulationSchema.safeParse(simulation);
    if (!result.success) {
        const errors = result.error.issues.map(
            (i) => `${i.path.join('.')}: ${i.message}`,
        );
        console.warn('[buildSimulation] invalid output:', errors);
        return null;
    }

    return simulation;
}

export interface AhaMomentResult {
    image: AhaMomentImage;
    headlineType: HeadlineType;
    text: string;
    date: string;
    moneyMap: MoneyMapNumbers;
    simulation: MoneyMapSimulation | null;
    slides: ResultSlide[];
}

/**
 * Single entry point for computing the aha moment display data.
 * Calls buildSimulation internally, runs it, and extracts the milestone date.
 */
export function computeAhaMoment(intake: AhaMomentIntake): AhaMomentResult {
    const {
        monthlyTakeHomeCents: incomeTotalCents,
        monthlyExpensesCents: billsTotalCents,
        monthlySpendCents: spendTotalCents,
    } = intake;

    const goalsTotalCents = Math.max(
        incomeTotalCents - billsTotalCents - spendTotalCents,
        0,
    );

    const moneyMap: MoneyMapNumbers = {
        incomeTotalCents,
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
    };

    if (
        intake.branch === CMMBranch.StartInvesting &&
        intake.investment != null
    ) {
        const waterfall = buildInvestmentWaterfall(intake.investment);
        return {
            image: AhaMomentImage.Investing,
            headlineType: HeadlineType.InvestingBy,
            text: `${formatDollarsLocal(waterfall.projectedTotalCents / 100, false)} invested by`,
            date: waterfall.projectedDate,
            moneyMap,
            simulation: null,
            slides: BRANCH_SLIDES[CMMBranch.StartInvesting],
        };
    }

    const effectiveIntake: AhaMomentIntake = {
        ...intake,
        monthlyGoalsSpendCents: goalsTotalCents,
    };

    const simulation = buildSimulation(effectiveIntake);

    const params: SimulationParams = {
        startDate: new Date(),
        numMonths: MAX_SIMULATION_MONTHS,
        depositApyPercent: DEPOSIT_APY_PERCENT,
        investmentReturnPercent: INVESTMENT_RETURN_PERCENT,
    };

    const ruleSetCount = simulation?.ruleSets.length ?? 0;
    const filterGoalSlides = (slides: ResultSlide[]): ResultSlide[] => {
        if (ruleSetCount > 0) return slides;
        return slides.filter(
            (s) =>
                s !== ResultSlide.GoalsIntro &&
                s !== ResultSlide.FinancialHealth &&
                s !== ResultSlide.SavingsGoals &&
                s !== ResultSlide.Goals,
        );
    };

    const goalsSystemHighInterest =
        intake.branch === CMMBranch.GoalsSystem &&
        (intake.debts ?? []).some(
            (d) =>
                d.annualInterestRatePercent != null &&
                d.annualInterestRatePercent / 100 > HIGH_INTEREST_THRESHOLD,
        );
    const isSavingsPath =
        intake.branch === CMMBranch.GoalsSavings ||
        (intake.branch === CMMBranch.GoalsSystem && !goalsSystemHighInterest) ||
        (intake.branch === CMMBranch.MoneySystem && intake.savings != null) ||
        (intake.branch === CMMBranch.GoalsDebt &&
            (intake.debts == null || intake.debts.length === 0));

    const isMoneySystem = intake.branch === CMMBranch.MoneySystem;
    const branchImage = isMoneySystem
        ? AhaMomentImage.Management
        : isSavingsPath
          ? AhaMomentImage.SecureSavings
          : AhaMomentImage.DiscoverBw;

    if (isSavingsPath) {
        const textBase = formatSaveForGoalLabel(
            pickHighestTargetGoal(intake.savings)?.description,
        );
        const date = findSavingsGoalDate(intake, simulation, params);
        const headlineType =
            date != null
                ? HeadlineType.SaveForGoalBy
                : HeadlineType.PrimaryGoal;
        const text = date != null ? `${textBase} by` : textBase;
        return {
            image: branchImage,
            headlineType,
            text,
            date: date ?? '',
            moneyMap,
            simulation,
            slides: filterGoalSlides(
                BRANCH_SLIDES[intake.branch ?? CMMBranch.GoalsSavings],
            ),
        };
    }

    const noGoalsMoney = goalsTotalCents <= 0;
    const date = findDebtFreeDate(simulation, params);

    if (noGoalsMoney) {
        const primaryGoal = derivePrimaryDebtGoalName(intake);
        return {
            image: branchImage,
            headlineType: HeadlineType.PrimaryGoal,
            text: primaryGoal,
            date: '',
            moneyMap,
            simulation,
            slides: filterGoalSlides(
                BRANCH_SLIDES[intake.branch ?? CMMBranch.GoalsDebt],
            ),
        };
    }

    const debtGoalName = derivePrimaryDebtGoalName(intake);
    return {
        image: branchImage,
        headlineType:
            date != null ? HeadlineType.DebtFreeBy : HeadlineType.PrimaryGoal,
        text: date != null ? 'Debt free by' : debtGoalName,
        date: date ?? '',
        moneyMap,
        simulation,
        slides: filterGoalSlides(
            BRANCH_SLIDES[intake.branch ?? CMMBranch.GoalsDebt],
        ),
    };
}

/* ─── Branch builders (private) ─── */

function buildDebtSimulation(
    intake: DebtBranchIntake,
): MoneyMapSimulation | null {
    const debts = intake.debts;

    const allDebtAccounts: DebtAccount[] = debts.map((debt, i) => ({
        id: `debt-${i}`,
        name: debt.description ?? `Debt ${i + 1}`,
        type: 'DEBT' as const,
        balanceCents: debt.balanceRemainingCents ?? 0,
        annualInterestRate: (debt.annualInterestRatePercent ?? 0) / 100,
        minimumPaymentCents: debt.minimumPaymentCents,
    }));

    const debtAccountsWithBalance = allDebtAccounts.filter(
        (d) => d.balanceCents > 0,
    );

    const highInterestDebts = debtAccountsWithBalance.filter(
        (d) => d.annualInterestRate > HIGH_INTEREST_THRESHOLD,
    );
    const targetDebts =
        highInterestDebts.length > 0
            ? highInterestDebts
            : debtAccountsWithBalance;

    const sortedByRate = [...targetDebts].sort(
        (a, b) => b.annualInterestRate - a.annualInterestRate,
    );

    const hasDebts = targetDebts.length > 0;
    const hasHighInterest = highInterestDebts.length > 0;

    const billsPlusSpend =
        intake.monthlyExpensesCents + intake.monthlySpendCents;

    const accounts: Account[] = [
        {
            id: 'goals-pool',
            name: 'Goals Pool',
            type: 'CHECKING' as const,
            balanceCents: 0,
        },
        {
            id: 'starter-ef',
            name: 'Emergency Fund',
            type: 'SAVINGS' as const,
            balanceCents: 0,
        },
        {
            id: 'full-ef',
            name: 'Full Emergency Fund',
            type: 'SAVINGS' as const,
            balanceCents: 0,
        },
        {
            id: 'retirement',
            name: 'Retirement',
            type: 'ROTH_IRA' as const,
            balanceCents: 0,
            annualContributionLimitCents: IRA_ANNUAL_LIMIT_CENTS,
            initialYtdContributionCents: 0,
        },
        ...targetDebts,
    ];

    const splits = getScenarioSplits({
        hasDebts,
        hasSavings: false,
        hasHighInterest,
    });

    const financialHealthRules: Rule[] = [];

    if (hasDebts) {
        financialHealthRules.push({
            id: 'rule-debt-avalanche',
            type: RuleType.DebtPayDown,
            description: hasHighInterest
                ? 'Pay off high-interest debt'
                : 'Pay off debt',
            priority: 1,
            accountIds: sortedByRate.map((d) => d.id),
            allocationType: 'DEBT_PAY_DOWN' as const,
            strategy: 'AVALANCHE' as const,
            splitPercentage: splits.debt,
            targetBalanceCents: allDebtAccounts.reduce(
                (sum, d) => sum + d.balanceCents,
                0,
            ),
        });
    }

    financialHealthRules.push(
        {
            id: 'rule-full-ef',
            type: RuleType.FullEmergencyFund,
            description: 'Build 3-month emergency fund',
            priority: 2,
            accountId: 'full-ef',
            allocationType: 'MINIMUM_BALANCE' as const,
            targetBalanceCents: 3 * billsPlusSpend,
            splitPercentage: splits.ef,
        },
        {
            id: 'rule-retirement',
            type: RuleType.Retirement,
            description: 'Retirement contributions',
            priority: 3,
            accountId: 'retirement',
            allocationType: 'PERCENTAGE' as const,
            percentage: 1.0,
            yearlyContributionMaxCents: IRA_ANNUAL_LIMIT_CENTS,
            splitPercentage: splits.retirement,
        },
    );

    return {
        monthlyGoalsCents: intake.monthlyGoalsSpendCents,
        incomeAccountId: 'goals-pool',
        accounts,
        ruleSets: [
            {
                id: 'rs-starter-ef',
                name: 'Emergency Fund',
                priority: 1,
                type: RuleSetType.EmergencyFund,
                rules: [
                    {
                        id: 'rule-starter-ef',
                        type: RuleType.StarterEmergencyFund,
                        description: 'Build 1-month emergency fund',
                        priority: 1,
                        accountId: 'starter-ef',
                        allocationType: 'MINIMUM_BALANCE' as const,
                        targetBalanceCents: billsPlusSpend,
                        splitPercentage: 1.0,
                    },
                ],
            },
            {
                id: 'rs-financial-health',
                name: 'Financial Health',
                priority: 2,
                type: RuleSetType.FinancialHealth,
                rules: financialHealthRules,
            },
        ],
    };
}

function buildSavingsSimulation(
    intake: SavingsBranchIntake,
): MoneyMapSimulation | null {
    const goal = pickHighestTargetGoal(intake.savings);
    if (goal == null) return null;

    const billsPlusSpend =
        intake.monthlyExpensesCents + intake.monthlySpendCents;

    const debts = intake?.debts;
    const hasDebt = (debts ?? []).length > 0;

    if (hasDebt && debts != null) {
        const allDebtAccounts: DebtAccount[] = debts.map((debt, i) => ({
            id: `debt-${i}`,
            name: debt.description ?? `Debt ${i + 1}`,
            type: 'DEBT' as const,
            balanceCents: debt.balanceRemainingCents ?? 0,
            annualInterestRate: (debt.annualInterestRatePercent ?? 0) / 100,
            minimumPaymentCents: debt.minimumPaymentCents,
        }));

        const highInterestDebts = allDebtAccounts.filter(
            (d) => d.annualInterestRate > HIGH_INTEREST_THRESHOLD,
        );
        const targetDebts =
            highInterestDebts.length > 0 ? highInterestDebts : allDebtAccounts;
        const sortedByRate = [...targetDebts].sort(
            (a, b) => b.annualInterestRate - a.annualInterestRate,
        );

        const hasHighInterest = highInterestDebts.length > 0;
        const splits = getScenarioSplits({
            hasDebts: true,
            hasSavings: true,
            hasHighInterest,
        });

        const debtRule: Rule = {
            id: 'rule-debt-avalanche',
            type: RuleType.DebtPayDown,
            description: hasHighInterest
                ? 'Pay off high-interest debt'
                : 'Pay off debt',
            priority: 1,
            accountIds: sortedByRate.map((d) => d.id),
            allocationType: 'DEBT_PAY_DOWN' as const,
            strategy: 'AVALANCHE' as const,
            splitPercentage: splits.debt,
            targetBalanceCents: allDebtAccounts.reduce(
                (sum, d) => sum + d.balanceCents,
                0,
            ),
        };

        const accounts: Account[] = [
            {
                id: 'goals-pool',
                name: 'Goals Pool',
                type: 'CHECKING' as const,
                balanceCents: 0,
            },
            {
                id: 'starter-ef',
                name: 'Emergency Fund',
                type: 'SAVINGS' as const,
                balanceCents: 0,
            },
            {
                id: 'full-ef',
                name: 'Full Emergency Fund',
                type: 'SAVINGS' as const,
                balanceCents: 0,
            },
            {
                id: 'savings-goal',
                name: goal.description ?? 'Savings Goal',
                type: 'SAVINGS' as const,
                balanceCents: goal.currentAmountCents,
            },
            {
                id: 'retirement',
                name: 'Retirement',
                type: 'ROTH_IRA' as const,
                balanceCents: 0,
                annualContributionLimitCents: IRA_ANNUAL_LIMIT_CENTS,
                initialYtdContributionCents: 0,
            },
            ...targetDebts,
        ];

        const financialHealthRules: Rule[] = [
            debtRule,
            {
                id: 'rule-savings-goal',
                type: RuleType.Savings,
                description: formatSaveForGoalLabel(goal.description),
                priority: 2,
                accountId: 'savings-goal',
                allocationType: 'MINIMUM_BALANCE' as const,
                targetBalanceCents: goal.targetAmountCents,
                splitPercentage: splits.savings,
            },
            {
                id: 'rule-full-ef',
                type: RuleType.FullEmergencyFund,
                description: 'Build 3-month emergency fund',
                priority: 3,
                accountId: 'full-ef',
                allocationType: 'MINIMUM_BALANCE' as const,
                targetBalanceCents: 3 * billsPlusSpend,
                splitPercentage: splits.ef,
            },
            {
                id: 'rule-retirement',
                type: RuleType.Retirement,
                description: 'Retirement contributions',
                priority: 4,
                accountId: 'retirement',
                allocationType: 'PERCENTAGE' as const,
                percentage: 1.0,
                yearlyContributionMaxCents: IRA_ANNUAL_LIMIT_CENTS,
                splitPercentage: splits.retirement,
            },
        ];

        return {
            monthlyGoalsCents: intake.monthlyGoalsSpendCents,
            incomeAccountId: 'goals-pool',
            accounts,
            ruleSets: [
                {
                    id: 'rs-starter-ef',
                    name: 'Emergency Fund',
                    priority: 1,
                    type: RuleSetType.EmergencyFund,
                    rules: [
                        {
                            id: 'rule-starter-ef',
                            type: RuleType.StarterEmergencyFund,
                            description: 'Build 1-month emergency fund',
                            priority: 1,
                            accountId: 'starter-ef',
                            allocationType: 'MINIMUM_BALANCE' as const,
                            targetBalanceCents: billsPlusSpend,
                            splitPercentage: 1.0,
                        },
                    ],
                },
                {
                    id: 'rs-financial-health',
                    name: 'Financial Health',
                    priority: 2,
                    type: RuleSetType.FinancialHealth,
                    rules: financialHealthRules,
                },
            ],
        };
    }

    const savingsOnlySplits = getScenarioSplits({
        hasDebts: false,
        hasSavings: true,
        hasHighInterest: false,
    });

    return {
        monthlyGoalsCents: intake.monthlyGoalsSpendCents,
        incomeAccountId: 'goals-pool',
        accounts: [
            {
                id: 'goals-pool',
                name: 'Goals Pool',
                type: 'CHECKING' as const,
                balanceCents: 0,
            },
            {
                id: 'starter-ef',
                name: 'Emergency Fund',
                type: 'SAVINGS' as const,
                balanceCents: 0,
            },
            {
                id: 'full-ef',
                name: 'Full Emergency Fund',
                type: 'SAVINGS' as const,
                balanceCents: 0,
            },
            {
                id: 'savings-goal',
                name: goal.description ?? 'Savings Goal',
                type: 'SAVINGS' as const,
                balanceCents: goal.currentAmountCents,
            },
            {
                id: 'retirement',
                name: 'Retirement',
                type: 'ROTH_IRA' as const,
                balanceCents: 0,
                annualContributionLimitCents: IRA_ANNUAL_LIMIT_CENTS,
                initialYtdContributionCents: 0,
            },
        ],
        ruleSets: [
            {
                id: 'rs-starter-ef',
                name: 'Emergency Fund',
                priority: 1,
                type: RuleSetType.EmergencyFund,
                rules: [
                    {
                        id: 'rule-starter-ef',
                        type: RuleType.StarterEmergencyFund,
                        description: 'Build 1-month emergency fund',
                        priority: 1,
                        accountId: 'starter-ef',
                        allocationType: 'MINIMUM_BALANCE' as const,
                        targetBalanceCents: billsPlusSpend,
                        splitPercentage: 1.0,
                    },
                ],
            },
            {
                id: 'rs-financial-health',
                name: 'Financial Health',
                priority: 2,
                type: RuleSetType.FinancialHealth,
                rules: [
                    {
                        id: 'rule-full-ef',
                        type: RuleType.FullEmergencyFund,
                        description: 'Build 3-month emergency fund',
                        priority: 1,
                        accountId: 'full-ef',
                        allocationType: 'MINIMUM_BALANCE' as const,
                        targetBalanceCents: 3 * billsPlusSpend,
                        splitPercentage: savingsOnlySplits.ef,
                    },
                    {
                        id: 'rule-savings-goal',
                        type: RuleType.Savings,
                        description: formatSaveForGoalLabel(goal.description),
                        priority: 1,
                        accountId: 'savings-goal',
                        allocationType: 'MINIMUM_BALANCE' as const,
                        targetBalanceCents: goal.targetAmountCents,
                        splitPercentage: savingsOnlySplits.savings,
                    },
                    {
                        id: 'rule-retirement',
                        type: RuleType.Retirement,
                        description: 'Retirement contributions',
                        priority: 1,
                        accountId: 'retirement',
                        allocationType: 'PERCENTAGE' as const,
                        percentage: 1.0,
                        yearlyContributionMaxCents: IRA_ANNUAL_LIMIT_CENTS,
                        splitPercentage: savingsOnlySplits.retirement,
                    },
                ],
            },
        ],
    };
}

/**
 * Goals-system always models both debt and savings when present.
 * The aha headline logic picks which date to feature (debt-free vs savings goal).
 */
function buildSystemSimulation(
    intake: AhaMomentIntake,
): MoneyMapSimulation | null {
    const { savings, debts, debtTypes } = intake;

    if (savings != null && savings.length > 0) {
        return buildSavingsSimulation({ ...intake, savings });
    }

    if (debts != null && debtTypes != null) {
        return buildDebtSimulation({ ...intake, debts, debtTypes });
    }

    return null;
}

/* ─── Headline helpers (private) ─── */

function derivePrimaryDebtGoalName(intake: AhaMomentIntake): string {
    const debts = intake.debts ?? [];
    const hasHighInterest = debts.some(
        (d) =>
            d.annualInterestRatePercent != null &&
            d.annualInterestRatePercent / 100 > HIGH_INTEREST_THRESHOLD,
    );
    return hasHighInterest ? 'Pay off high-interest debt' : 'Pay off debt';
}

/* ─── Date finders (private) ─── */

function findDebtFreeDate(
    simulation: MoneyMapSimulation | null,
    params: SimulationParams,
): string | null {
    if (!simulation) return null;

    const result = runSimulation(simulation, params);
    const debtAccountIds = simulation.accounts
        .filter((a) => a.type === 'DEBT')
        .map((a) => a.id);

    if (debtAccountIds.length === 0) return null;

    for (const month of result.months) {
        const allPaidOff = debtAccountIds.every(
            (id) => (month.balancesAfter[id] ?? 0) <= 0,
        );
        if (allPaidOff) {
            return month.date.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
            });
        }
    }

    return null;
}

function findSavingsGoalDate(
    intake: AhaMomentIntake,
    simulation: MoneyMapSimulation | null,
    params: SimulationParams,
): string | null {
    const goal = pickHighestTargetGoal(intake.savings);
    if (goal == null) return null;

    const remainingCents = goal.targetAmountCents - goal.currentAmountCents;
    if (remainingCents <= 0) return 'Already reached!';

    if (!simulation) return null;

    const result = runSimulation(simulation, params);

    for (const month of result.months) {
        if (
            (month.balancesAfter['savings-goal'] ?? 0) >= goal.targetAmountCents
        ) {
            return month.date.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
            });
        }
    }

    return null;
}
